import { Close, Save } from '@mui/icons-material';
import { Box, Button, Drawer, IconButton, InputLabel, TextField, Typography, CircularProgress } from '@mui/material';
import UserApiService from 'api/UserApiService';
import { Loader } from 'components';
import useClientsRights from 'hooks/rights/useClientsRights';
import useApi from 'hooks/useApi';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'store/hooks';
import Customer from 'types/entities/Customer';
import User from 'types/entities/User';
import { ActionDescriptor } from 'types/enums/ActionDescriptor';
import Tenant from 'types/entities/Tenant';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import useUserRoles from 'hooks/useUserRoles';

type FormData = {
  customer_id: number;
  department: string;
  direction: string;
  first_name: string;
  last_name: string;
  mail: string;
  phone: string;
  position: string;
  have_cognito_account: boolean;
  tenants: Tenant[];
};

type Props = {
  activeCustomer: Customer;
  activeClient: User;
  open: boolean;
  dispatchOpen: React.DispatchWithoutAction;
  setClient: (client: User) => void;
  adminTenants: Tenant[];
};

const ClientEdition: React.FC<Props> = ({
  activeClient,
  activeCustomer,
  adminTenants,
  open,
  dispatchOpen,
  setClient,
}: Props) => {
  const appState = useSelector((state) => state.app);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [client, setClientToUpdate] = useState<User>(activeClient);
  const { makeCall } = useApi();
  const { canEdit } = useClientsRights();
  const [t] = useTranslation();
  const userRoles = useUserRoles();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: client,
  });

  useEffect(() => {
    setClientToUpdate(activeClient);

    const selectedTenants = adminTenants.filter((adminTenant) =>
      (activeClient.tenants || []).some(
        (clientTenant) => clientTenant.customer_id === adminTenant.id && !clientTenant.is_archived
      )
    );

    reset({
      ...activeClient,
      tenants: selectedTenants,
    });
  }, [activeClient, adminTenants, reset]);

  const onSubmit = async (data: FormData) => {
    if (activeCustomer && activeClient) {
      setIsLoading(true);

      const { tenants, ...clientData } = data;

      const transformedData = {
        ...clientData,
        customer_id: appState.customer?.id || activeCustomer.id,
        action_id: ActionDescriptor.UpdateClient,
        tenants: (tenants || []).map(({ id, name, slug }) => ({
          id,
          name,
          slug,
        })),
      };

      try {
        const { user_client } = await makeCall(
          UserApiService.updateClient(client.id, transformedData),
          'Unable to update client'
        );

        if (user_client !== undefined) {
          setClient(user_client);
          dispatchOpen();
        }
      } catch (error) {
        console.error('Update error:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={dispatchOpen}
      PaperProps={{
        sx: {
          width: {
            xs: '100%',
            lg: 450,
          },
        },
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 5 }}>
          <Typography variant="h2">{canEdit ? t('edit_client') : t('client')}</Typography>
          <IconButton
            sx={{ ml: 'auto' }}
            color="primary"
            onClick={dispatchOpen}
            data-testid="close_client_edition"
            disabled={isLoading} // ✅ Disable close button during loading
          >
            <Close />
          </IconButton>
        </Box>

        {/* ✅ Form is always visible, no conditional rendering */}
        <Box>
          <Controller
            name="first_name"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Box sx={{ mb: 3 }}>
                <InputLabel htmlFor="client_first_name" error={errors.first_name && true}>
                  First name{' '}
                  <Typography display="inline" color="error">
                    *
                  </Typography>
                </InputLabel>
                <TextField
                  id="client_first_name"
                  onChange={onChange}
                  value={value || ''}
                  fullWidth
                  disabled={!canEdit || isLoading} // ✅ Disable during loading
                  helperText={errors.first_name?.message}
                  error={errors.first_name && true}
                  data-testid="client_first_name"
                  inputProps={{ maxLength: 100 }}
                />
              </Box>
            )}
            rules={{
              required: {
                value: true,
                message: 'Your must enter a first name',
              },
              pattern: {
                value: /^[a-zA-ZÀ-ÿ\s'-]+$/,
                message: 'Special characters and numbers are not allowed',
              },
            }}
          />

          <Controller
            name="last_name"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Box sx={{ mb: 3 }}>
                <InputLabel htmlFor="client_last_name" error={errors.last_name && true}>
                  Last name{' '}
                  <Typography display="inline" color="error">
                    *
                  </Typography>
                </InputLabel>
                <TextField
                  id="client_last_name"
                  onChange={onChange}
                  value={value || ''}
                  fullWidth
                  disabled={!canEdit || isLoading} // ✅ Disable during loading
                  helperText={errors.last_name?.message}
                  error={errors.last_name && true}
                  data-testid="client_last_name"
                  inputProps={{ maxLength: 100 }}
                />
              </Box>
            )}
            rules={{
              required: {
                value: true,
                message: 'Your must enter a last name',
              },
              pattern: {
                value: /^[a-zA-ZÀ-ÿ\s'-]+$/,
                message: 'Special characters and numbers are not allowed',
              },
            }}
          />

          <Controller
            name="position"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Box sx={{ mb: 3 }}>
                <InputLabel htmlFor="client_position" error={errors.position && true}>
                  Position{' '}
                </InputLabel>
                <TextField
                  id="client_position"
                  onChange={onChange}
                  value={value || ''}
                  fullWidth
                  disabled={!canEdit || isLoading} //  Disable during loading
                  helperText={errors.position?.message}
                  error={errors.position && true}
                  data-testid="client_position"
                  inputProps={{ maxLength: 100 }}
                />
              </Box>
            )}
            rules={{
              pattern: {
                value: /^[a-zA-Z0-9\s\-'(),.&]+$/i,
                message: 'Special characters are not allowed',
              },
            }}
          />

          <Controller
            name="mail"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Box sx={{ mb: 3 }}>
                <InputLabel htmlFor="client_mail" error={errors.mail && true}>
                  Mail{' '}
                  <Typography display="inline" color="error">
                    *
                  </Typography>
                </InputLabel>
                <TextField
                  id="client_mail"
                  onChange={(e) => onChange(e.target.value.toLowerCase())}
                  value={value || ''}
                  fullWidth
                  disabled={!canEdit || isLoading} //  Disable during loading
                  helperText={errors.mail?.message}
                  error={errors.mail && true}
                  data-testid="client_mail"
                  inputProps={{ maxLength: 254 }}
                />
              </Box>
            )}
            rules={{
              required: {
                value: true,
                message: 'Your must enter an email',
              },
              pattern: {
                value:
                  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i, // eslint-disable-line max-len
                message: 'Please enter a valid email',
              },
            }}
          />

          <Controller
            name="department"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Box sx={{ mb: 3 }}>
                <InputLabel htmlFor="client_department" error={errors.department && true}>
                  Department{' '}
                </InputLabel>
                <TextField
                  id="client_department"
                  onChange={onChange}
                  value={value || ''}
                  fullWidth
                  disabled={!canEdit || isLoading} //  Disable during loading
                  helperText={errors.department?.message}
                  error={errors.department && true}
                  data-testid="client_department"
                  inputProps={{ maxLength: 100 }}
                />
              </Box>
            )}
            rules={{
              pattern: {
                value: /^[a-zA-Z0-9\s\-'().&]+$/i,
                message: 'Special characters are not allowed',
              },
            }}
          />

          <Controller
            name="direction"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Box sx={{ mb: 3 }}>
                <InputLabel htmlFor="client_direction" error={errors.direction && true}>
                  Direction{' '}
                </InputLabel>
                <TextField
                  id="client_direction"
                  onChange={onChange}
                  value={value || ''}
                  fullWidth
                  disabled={!canEdit || isLoading} //  Disable during loading
                  helperText={errors.direction?.message}
                  error={errors.direction && true}
                  data-testid="client_direction"
                  inputProps={{ maxLength: 100 }}
                />
              </Box>
            )}
            rules={{
              pattern: {
                value: /^[a-zA-Z0-9\s\-'().&]+$/i,
                message: 'Special characters are not allowed',
              },
            }}
          />
          {userRoles.isAdmin && (
            <Controller
              name="tenants"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  multiple
                  options={adminTenants}
                  value={value}
                  onChange={(_, newValue) => onChange(newValue)}
                  getOptionLabel={(option: Tenant) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  disableCloseOnSelect
                  disableClearable
                  disabled={!canEdit || isLoading} //  Disable during loading
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox style={{ marginRight: 8 }} checked={selected} />
                      {option.name}
                    </li>
                  )}
                  renderTags={() => null}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select tenants"
                      fullWidth
                      disabled={!canEdit || isLoading} //  Disable during loading
                    />
                  )}
                />
              )}
            />
          )}

          {canEdit && (
            <Box sx={{ mt: 2 }}>
              <Button
                startIcon={isLoading ? null : <Save />} //  Hide icon when loading
                onClick={handleSubmit(onSubmit)}
                variant="contained"
                color="success"
                fullWidth
                data-testid="client_submit"
                disabled={isLoading} //  Disable button when loading
                sx={{
                  minHeight: 56, //  Maintain button height
                }}
              >
                {isLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    <span>Updating...</span>
                  </Box>
                ) : (
                  'Update client'
                )}
              </Button>
            </Box>
          )}
        </Box>
      </form>
    </Drawer>
  );
};

export default ClientEdition;
